import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Ticket, Plus, X, Calendar, Users, ArrowRight } from "lucide-react";

const STATUS_COLORS = {
  draft:     "text-foreground/50 border-foreground/20",
  published: "text-emerald-500 border-emerald-500/40",
  live:      "text-red-500 border-red-500/40 animate-pulse",
  closed:    "text-foreground/40 border-foreground/15",
  cancelled: "text-red-500 border-red-500/40 opacity-60",
};

export default function EventsListPage() {
  const { branchId, hasPerm } = useAuth();
  const [events, setEvents] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const params = { limit: 50 };
      if (branchId) params.branch_id = branchId;
      const { data } = await api.get("/events", { params });
      setEvents(data);
    } catch (_) {}
    finally { setLoading(false); }
  };
  useEffect(() => { setLoading(true); load(); }, [branchId]);

  const filtered = useMemo(() => {
    if (filter === "all") return events;
    if (filter === "upcoming") return events.filter((e) => new Date(e.starts_at) >= new Date() && e.status !== "closed" && e.status !== "cancelled");
    return events.filter((e) => e.status === filter);
  }, [events, filter]);

  const totals = useMemo(() => ({
    revenue: events.reduce((s, e) => s + (e.ticket_revenue || 0), 0),
    sold:    events.reduce((s, e) => s + (e.tickets_sold || 0), 0),
    upcoming: events.filter((e) => new Date(e.starts_at) >= new Date() && e.status !== "closed" && e.status !== "cancelled").length,
  }), [events]);

  const submit = async (e) => {
    e.preventDefault();
    if (!branchId) { toast.error("Select a branch first"); return; }
    try {
      const { data } = await api.post("/events", {
        branch_id: branchId,
        name: form.name,
        description: form.description,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at:   new Date(form.ends_at).toISOString(),
        capacity:  parseInt(form.capacity) || 0,
        promoter_commission_pct: parseFloat(form.promoter_commission_pct) || 0,
        guest_commission_flat:   parseFloat(form.guest_commission_flat) || 0,
      });
      toast.success(`Event "${data.name}" created (draft)`);
      setCreating(false); setForm(BLANK); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="events-list-page">
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">Events · Nights &amp; parties</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Events</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Every ticketed night in one place. Sell tickets, curate guest lists, and let the promoter commissions calculate themselves when you close the door.
          </p>
        </div>
        {hasPerm("event.manage") && (
          <button onClick={() => setCreating(true)} data-testid="event-new-btn" className="btn-pill btn-pill-solid h-10">
            <Plus size={14}/> New event
          </button>
        )}
      </div>

      <div className="mt-14 grid grid-cols-3 gap-x-12 gap-y-6 animate-fade-up animate-fade-up-1">
        <Stat num=".01" label="Upcoming"       value={totals.upcoming}/>
        <Stat num=".02" label="Tickets sold"   value={totals.sold}/>
        <Stat num=".03" label="Ticket revenue" value={peso(totals.revenue)} accent/>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-2 animate-fade-up animate-fade-up-2">
        {["all","upcoming","draft","published","live","closed","cancelled"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
                  data-testid={`event-filter-${f}`}
                  className={`h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.24em] font-bold border transition-colors ${
                    filter === f ? "bg-foreground text-background border-foreground" : "hairline text-foreground/60 hover:text-foreground"
                  }`}>{f}</button>
        ))}
      </div>

      {loading && <div className="mt-10 ink-subtle text-sm">Loading…</div>}

      {!loading && filtered.length === 0 && (
        <div className="mt-10 border hairline-soft rounded-md p-16 text-center" data-testid="event-empty">
          <Ticket size={40} className="mx-auto ink" strokeWidth={1.2}/>
          <div className="mt-5 display-heading text-2xl ink">No events yet.</div>
          <div className="mt-2 ink-subtle text-sm">
            {hasPerm("event.manage") ? "Click New event to start planning your first night." : "Ask a manager to create the next event."}
          </div>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-4 animate-fade-up animate-fade-up-3">
          {filtered.map((e) => {
            const dt = new Date(e.starts_at);
            const capPct = e.capacity ? Math.round((e.tickets_sold / e.capacity) * 100) : 0;
            return (
              <Link key={e.id} to={`/events/${e.id}`} data-testid={`event-card-${e.id}`}
                    className="border hairline surface-card rounded-lg p-5 hover:border-foreground/50 transition-colors group">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] uppercase tracking-[0.24em] font-bold px-2 py-0.5 rounded-full border ${STATUS_COLORS[e.status]}`}>
                        {e.status}
                      </span>
                      <span className="tiny-label ink-subtle mono">
                        {dt.toLocaleDateString("en-PH", { weekday: "short", day: "2-digit", month: "short" })} · {dt.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    <div className="mt-3 display-heading text-2xl ink truncate">{e.name}</div>
                    {e.description && (
                      <div className="mt-1 text-xs ink-subtle line-clamp-2">{e.description}</div>
                    )}
                  </div>
                  <ArrowRight size={16} className="ink-subtle group-hover:ink group-hover:translate-x-1 transition-all shrink-0"/>
                </div>

                <div className="mt-5 grid grid-cols-3 gap-3 pt-4 border-t hairline-soft">
                  <div>
                    <div className="tiny-label ink-subtle">Sold</div>
                    <div className="mt-1 mono ink text-lg font-semibold">{e.tickets_sold}<span className="ink-subtle text-xs"> / {e.capacity || "∞"}</span></div>
                  </div>
                  <div>
                    <div className="tiny-label ink-subtle">Revenue</div>
                    <div className="mt-1 mono ink text-lg font-semibold">{peso(e.ticket_revenue)}</div>
                  </div>
                  <div>
                    <div className="tiny-label ink-subtle">Capacity</div>
                    <div className="mt-1 mono ink text-lg font-semibold">{capPct}%</div>
                    <div className="mt-1 h-1 rounded-full bg-foreground/10 overflow-hidden">
                      <div className={`h-full ${capPct > 90 ? "bg-red-500" : capPct > 60 ? "bg-amber-500" : "bg-foreground"}`} style={{ width: `${Math.min(capPct, 100)}%` }}/>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Create drawer */}
      {creating && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="event-new-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setCreating(false)}/>
          <form onSubmit={submit} className="relative w-full max-w-md surface-2 border-l hairline h-full p-6 overflow-y-auto shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <div className="section-eyebrow">Events</div>
                <h2 className="mt-2 display-heading text-2xl ink">Plan a new event</h2>
                <p className="mt-2 ink-muted text-xs">Event opens in <span className="mono ink">draft</span>. Publish it once ticket types are ready.</p>
              </div>
              <button type="button" onClick={() => setCreating(false)} className="tiny-label ink-subtle hover:ink"><X size={16}/></button>
            </div>

            <F label="Event name"><input required autoFocus value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="i" data-testid="event-form-name" placeholder="Midnight Run"/></F>
            <F label="Description"><textarea rows={2} value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} className="i-tall" data-testid="event-form-desc" placeholder="One-liner for the flyer / socials"/></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Starts at"><input required type="datetime-local" value={form.starts_at} onChange={(e)=>setForm({...form, starts_at: e.target.value})} className="i mono" data-testid="event-form-starts"/></F>
              <F label="Ends at"><input required type="datetime-local" value={form.ends_at} onChange={(e)=>setForm({...form, ends_at: e.target.value})} className="i mono" data-testid="event-form-ends"/></F>
            </div>
            <F label="Capacity (total)"><input type="number" value={form.capacity} onChange={(e)=>setForm({...form, capacity: e.target.value})} className="i mono" data-testid="event-form-cap"/></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Promoter % (fallback)"><input type="number" step="0.1" value={form.promoter_commission_pct} onChange={(e)=>setForm({...form, promoter_commission_pct: e.target.value})} className="i mono" data-testid="event-form-pct"/></F>
              <F label="Per-guest flat (₱)"><input type="number" step="1" value={form.guest_commission_flat} onChange={(e)=>setForm({...form, guest_commission_flat: e.target.value})} className="i mono" data-testid="event-form-flat"/></F>
            </div>

            <div className="mt-6 flex gap-2">
              <button type="button" onClick={() => setCreating(false)} className="btn-pill btn-pill-outline h-11 flex-1">Cancel</button>
              <button type="submit" data-testid="event-form-submit" className="btn-pill btn-pill-solid h-11 flex-1"><Calendar size={13}/> Create draft</button>
            </div>

            <style>{`.i { margin-top: 6px; height: 42px; width: 100%; border-radius: 8px; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); padding: 0 14px; color: hsl(var(--foreground)); font-size: 13px; }
                     .i-tall { margin-top: 6px; width: 100%; border-radius: 8px; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); padding: 10px 14px; color: hsl(var(--foreground)); font-size: 13px; }`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

const BLANK = { name: "", description: "", starts_at: "", ends_at: "", capacity: 200, promoter_commission_pct: 8, guest_commission_flat: 50 };

function Stat({ num, label, value, accent }) {
  return (
    <div data-testid={`event-stat-${label.toLowerCase().replace(/[^\w]+/g,"-")}`}>
      <div className="flex items-center justify-between">
        <div className="section-eyebrow">{label}</div>
        <span className="section-num">{num}</span>
      </div>
      <div className={`mt-3 mono ink text-2xl font-semibold ${accent ? "border-l-2 border-foreground pl-3" : ""}`}>{value}</div>
    </div>
  );
}

function F({ label, children }) {
  return <label className="block mt-4"><div className="tiny-label">{label}</div>{children}</label>;
}
